import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/hooks/useCart";
import { Link } from "react-router-dom";

const CheckoutPage: React.FC = () => {
  const { items, subtotal } = useCart();
  const shipping = subtotal >= 200 ? 0 : 15.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center py-20">
          <i className="ri-shopping-cart-line text-6xl text-gray-300 block mb-4"></i>
          <p className="text-xl text-gray-500 mb-6">Your cart is empty</p>
          <Link to="/products" className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
            Browse Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h1 className="text-4xl font-black text-gray-900 mb-8">Checkout</h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Order Summary */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.productId} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-2">
                <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm text-gray-600"><span>Shipping</span><span>{shipping === 0 ? <span className="text-green-600">FREE</span> : `$${shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between text-sm text-gray-600"><span>Tax (8%)</span><span>${tax.toFixed(2)}</span></div>
                <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
                  <span>Total</span><span className="text-orange-500 text-xl">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Payment</h2>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                  <i className="ri-bank-card-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Stripe Secure Checkout</h3>
                <p className="text-gray-600 text-sm mb-4">
                  To complete your purchase, please connect Stripe payment processing. Your payment will be secured with industry-standard encryption.
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-4">
                  <i className="ri-shield-check-line text-green-500"></i>
                  <span>256-bit SSL encryption</span>
                  <span>•</span>
                  <i className="ri-lock-line text-green-500"></i>
                  <span>PCI DSS compliant</span>
                </div>
                <div className="bg-white rounded-lg p-4 border border-orange-100 text-left mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Accepted Payment Methods:</p>
                  <div className="flex flex-wrap gap-2">
                    {["Visa", "Mastercard", "Amex", "Discover", "Apple Pay", "Google Pay"].map((m) => (
                      <span key={m} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-medium">{m}</span>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-orange-600 bg-orange-100 rounded-lg p-3">
                  <i className="ri-information-line mr-1"></i>
                  Stripe payment integration is ready to be activated. Connect your Stripe account to enable live payments.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <i className="ri-truck-line text-orange-500"></i>
                  <span>{shipping === 0 ? "Free shipping on your order!" : `Standard shipping: $${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <i className="ri-refresh-line text-blue-500"></i>
                  <span>30-day hassle-free returns</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <i className="ri-customer-service-2-line text-green-500"></i>
                  <span>Support: strayloftllc@outlook.com</span>
                </div>
              </div>
              <Link to="/cart" className="block w-full mt-6 py-3 text-center border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-2"></i>Back to Cart
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
